/**
 * Email Logs Page - STORY-028
 *
 * Email history with statuses:
 * - sent, delivered, opened, bounced, failed
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Mail,
  Search,
  Filter,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Eye,
  BarChart3,
  RefreshCw,
  Send,
  MousePointer,
  ArrowDownCircle,
} from 'lucide-react'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { Pagination } from '@/components/ui/Pagination'
import { useEmailLogs, useEmailStats } from '@/hooks/useEmails'
import type { EmailLog, EmailStatus, EmailLogFilters } from '@/types/email'

// Status configurations
const statusConfig: Record<
  EmailStatus,
  { label: string; color: string; icon: React.ElementType }
> = {
  pending: {
    label: 'En attente',
    color: 'bg-gray-100 text-gray-700',
    icon: Clock,
  },
  queued: {
    label: 'En file',
    color: 'bg-gray-100 text-gray-700',
    icon: Clock,
  },
  sending: {
    label: 'Envoi...',
    color: 'bg-blue-100 text-blue-700',
    icon: RefreshCw,
  },
  sent: {
    label: 'Envoye',
    color: 'bg-green-100 text-green-700',
    icon: Send,
  },
  delivered: {
    label: 'Delivre',
    color: 'bg-green-100 text-green-700',
    icon: CheckCircle,
  },
  opened: {
    label: 'Ouvert',
    color: 'bg-blue-100 text-blue-700',
    icon: Eye,
  },
  clicked: {
    label: 'Clique',
    color: 'bg-purple-100 text-purple-700',
    icon: MousePointer,
  },
  bounced: {
    label: 'Rebondi',
    color: 'bg-amber-100 text-amber-700',
    icon: ArrowDownCircle,
  },
  failed: {
    label: 'Echec',
    color: 'bg-red-100 text-red-700',
    icon: XCircle,
  },
}

// Date filter options
const dateFilterOptions = [
  { label: "Aujourd'hui", value: 'today' },
  { label: '7 derniers jours', value: 'week' },
  { label: '30 derniers jours', value: 'month' },
  { label: '90 derniers jours', value: 'quarter' },
  { label: 'Tout', value: 'all' },
]

export default function EmailLogsPage() {
  const [filters, setFilters] = useState<EmailLogFilters>({
    page: 1,
    limit: 20,
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [dateFilter, setDateFilter] = useState('week')
  const [statusFilter, setStatusFilter] = useState<EmailStatus | 'all'>('all')
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)

  // Compute date range based on filter
  const getDateRange = () => {
    const now = new Date()
    let startDate: Date | null = null

    switch (dateFilter) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case 'quarter':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
        break
      default:
        startDate = null
    }

    return startDate ? startDate.toISOString() : undefined
  }

  // Build filters
  const computedFilters: EmailLogFilters = {
    ...filters,
    search: searchQuery || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    startDate: getDateRange(),
  }

  // Queries
  const { data: logsData, isLoading, error, refetch } = useEmailLogs(computedFilters)
  const { data: stats } = useEmailStats(getDateRange())

  const logs = logsData?.logs || []
  const totalLogs = logsData?.total || 0
  const totalPages = Math.ceil(totalLogs / (filters.limit || 20))

  // Handlers
  const handleSearch = (value: string) => {
    setSearchQuery(value)
    setFilters({ ...filters, page: 1 })
  }

  const handleStatusFilter = (status: EmailStatus | 'all') => {
    setStatusFilter(status)
    setFilters({ ...filters, page: 1 })
  }

  const handleDateFilter = (date: string) => {
    setDateFilter(date)
    setFilters({ ...filters, page: 1 })
  }

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page })
  }

  const openDetailModal = (log: EmailLog) => {
    setSelectedLog(log)
    setIsDetailModalOpen(true)
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-brown-600">Erreur lors du chargement des logs</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => refetch()}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Reessayer
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Historique des Emails
          </h1>
          <p className="text-brown-600 mt-1">
            Suivez le statut de tous les emails envoyes
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="text-center">
            <div className="text-2xl font-bold text-brown-900">
              {stats.total_sent + stats.total_delivered}
            </div>
            <div className="text-sm text-brown-600">Envoyes</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {stats.total_delivered}
            </div>
            <div className="text-sm text-brown-600">Delivres</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {stats.total_opened}
            </div>
            <div className="text-sm text-brown-600">Ouverts</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {stats.total_clicked}
            </div>
            <div className="text-sm text-brown-600">Cliques</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-amber-600">
              {stats.total_bounced}
            </div>
            <div className="text-sm text-brown-600">Rebondis</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {stats.total_failed}
            </div>
            <div className="text-sm text-brown-600">Echecs</div>
          </Card>
        </div>
      )}

      {/* Rates */}
      {stats && (
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-5 h-5 text-mint-600" />
            <CardTitle>Taux de performance</CardTitle>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-brown-600 mb-1">Taux de delivrabilite</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-beige-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${stats.delivery_rate}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-brown-900">
                  {stats.delivery_rate.toFixed(1)}%
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm text-brown-600 mb-1">Taux d'ouverture</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-beige-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500"
                    style={{ width: `${stats.open_rate}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-brown-900">
                  {stats.open_rate.toFixed(1)}%
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm text-brown-600 mb-1">Taux de clic</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-beige-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500"
                    style={{ width: `${stats.click_rate}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-brown-900">
                  {stats.click_rate.toFixed(1)}%
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm text-brown-600 mb-1">Taux de rebond</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-beige-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500"
                    style={{ width: `${stats.bounce_rate}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-brown-900">
                  {stats.bounce_rate.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Rechercher par email ou sujet..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Date filter */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-brown-500" />
              <select
                value={dateFilter}
                onChange={(e) => handleDateFilter(e.target.value)}
                className="px-3 py-2 border border-beige-300 rounded-lg text-sm focus:ring-2 focus:ring-mint-500"
              >
                {dateFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-brown-500" />
              <select
                value={statusFilter}
                onChange={(e) =>
                  handleStatusFilter(e.target.value as EmailStatus | 'all')
                }
                className="px-3 py-2 border border-beige-300 rounded-lg text-sm focus:ring-2 focus:ring-mint-500"
              >
                <option value="all">Tous les statuts</option>
                {Object.entries(statusConfig).map(([status, config]) => (
                  <option key={status} value={status}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </Card>

      {/* Logs Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      ) : logs.length > 0 ? (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-beige-50 border-b border-beige-200">
                <tr>
                  <th className="text-left text-xs font-medium text-brown-600 uppercase tracking-wider px-6 py-3">
                    Destinataire
                  </th>
                  <th className="text-left text-xs font-medium text-brown-600 uppercase tracking-wider px-6 py-3">
                    Sujet
                  </th>
                  <th className="text-left text-xs font-medium text-brown-600 uppercase tracking-wider px-6 py-3">
                    Statut
                  </th>
                  <th className="text-left text-xs font-medium text-brown-600 uppercase tracking-wider px-6 py-3">
                    Date
                  </th>
                  <th className="text-right text-xs font-medium text-brown-600 uppercase tracking-wider px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-100">
                {logs.map((log) => {
                  const StatusIcon = statusConfig[log.status]?.icon || Clock
                  return (
                    <motion.tr
                      key={log.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-beige-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-brown-900">
                            {log.recipient_name || '-'}
                          </div>
                          <div className="text-sm text-brown-500">
                            {log.recipient_email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-brown-900 max-w-md truncate">
                          {log.subject}
                        </div>
                        {log.template && (
                          <div className="text-xs text-brown-500">
                            Template: {log.template.name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={statusConfig[log.status]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[log.status]?.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-brown-600">
                          {formatDate(log.created_at)}
                        </div>
                        {log.sent_at && (
                          <div className="text-xs text-brown-500">
                            Envoye: {formatDate(log.sent_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetailModal(log)}
                          leftIcon={<Eye className="w-4 h-4" />}
                        >
                          Details
                        </Button>
                      </td>
                    </motion.tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-beige-200">
              <div className="text-sm text-brown-600">
                {totalLogs} email{totalLogs > 1 ? 's' : ''} au total
              </div>
              <Pagination
                currentPage={filters.page || 1}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </Card>
      ) : (
        <EmptyState
          icon={<Mail className="w-12 h-12" />}
          title="Aucun email"
          description="Aucun email ne correspond a vos criteres de recherche"
        />
      )}

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false)
          setSelectedLog(null)
        }}
        title="Details de l'email"
        size="xl"
      >
        {selectedLog && (
          <div className="space-y-6">
            {/* Status timeline */}
            <div className="flex items-center gap-4 flex-wrap">
              {selectedLog.created_at && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-brown-600">
                    Cree: {formatDate(selectedLog.created_at)}
                  </span>
                </div>
              )}
              {selectedLog.sent_at && (
                <div className="flex items-center gap-2 text-sm">
                  <Send className="w-4 h-4 text-green-500" />
                  <span className="text-brown-600">
                    Envoye: {formatDate(selectedLog.sent_at)}
                  </span>
                </div>
              )}
              {selectedLog.delivered_at && (
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-brown-600">
                    Delivre: {formatDate(selectedLog.delivered_at)}
                  </span>
                </div>
              )}
              {selectedLog.opened_at && (
                <div className="flex items-center gap-2 text-sm">
                  <Eye className="w-4 h-4 text-blue-500" />
                  <span className="text-brown-600">
                    Ouvert: {formatDate(selectedLog.opened_at)}
                  </span>
                </div>
              )}
              {selectedLog.clicked_at && (
                <div className="flex items-center gap-2 text-sm">
                  <MousePointer className="w-4 h-4 text-purple-500" />
                  <span className="text-brown-600">
                    Clique: {formatDate(selectedLog.clicked_at)}
                  </span>
                </div>
              )}
              {selectedLog.bounced_at && (
                <div className="flex items-center gap-2 text-sm">
                  <ArrowDownCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-brown-600">
                    Rebondi: {formatDate(selectedLog.bounced_at)}
                  </span>
                </div>
              )}
              {selectedLog.failed_at && (
                <div className="flex items-center gap-2 text-sm">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="text-brown-600">
                    Echec: {formatDate(selectedLog.failed_at)}
                  </span>
                </div>
              )}
            </div>

            {/* Email info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-beige-50 p-4 rounded-lg">
                <div className="text-sm text-brown-600 mb-1">Destinataire</div>
                <div className="font-medium text-brown-900">
                  {selectedLog.recipient_name || '-'}
                </div>
                <div className="text-sm text-brown-600">
                  {selectedLog.recipient_email}
                </div>
              </div>
              <div className="bg-beige-50 p-4 rounded-lg">
                <div className="text-sm text-brown-600 mb-1">Statut</div>
                <Badge className={statusConfig[selectedLog.status]?.color}>
                  {statusConfig[selectedLog.status]?.label}
                </Badge>
                {selectedLog.retry_count > 0 && (
                  <div className="text-xs text-brown-500 mt-1">
                    Tentatives: {selectedLog.retry_count}/{selectedLog.max_retries}
                  </div>
                )}
              </div>
            </div>

            {/* Subject */}
            <div className="bg-beige-50 p-4 rounded-lg">
              <div className="text-sm text-brown-600 mb-1">Sujet</div>
              <div className="font-medium text-brown-900">{selectedLog.subject}</div>
            </div>

            {/* Error message */}
            {selectedLog.error_message && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 mb-1">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Erreur</span>
                </div>
                <div className="text-sm text-red-600">{selectedLog.error_message}</div>
              </div>
            )}

            {/* Content preview */}
            <div>
              <div className="text-sm font-medium text-brown-700 mb-2">
                Contenu de l'email
              </div>
              <div
                className="border border-beige-200 rounded-lg p-4 bg-white max-h-96 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: selectedLog.html_content }}
              />
            </div>

            {/* Reference info */}
            {(selectedLog.reference_type || selectedLog.reference_id) && (
              <div className="bg-beige-50 p-4 rounded-lg">
                <div className="text-sm text-brown-600 mb-1">Reference</div>
                <div className="text-sm text-brown-900">
                  {selectedLog.reference_type} - {selectedLog.reference_id}
                </div>
              </div>
            )}

            {/* Metadata */}
            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
              <div className="bg-beige-50 p-4 rounded-lg">
                <div className="text-sm text-brown-600 mb-2">Metadonnees</div>
                <pre className="text-xs text-brown-900 overflow-x-auto">
                  {JSON.stringify(selectedLog.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
